export type DayOfMonth = {
    /** The date, 1 to 31. */
    number: number;
    /** English ordinal. Shown in the "Day" column, and the key the Portuguese lesson file translates. */
    day: string;
    kanji: string;
    /**
     * Reading in hiragana. It is both what the table shows and the exact text the play buttons speak: the
     * browser voice reads a kanji date unreliably (一日 comes out as いちにち, not ついたち), so never pass
     * the kanji to the speech function.
     */
    reading: string;
    romaji: string;
    /** True for the native Japanese readings that follow no rule: 1st-10th, 14th, 20th and 24th. */
    irregular: boolean;
};

/**
 * Every date in a month, in order, for Lesson 7 ("Days of the month"). The lesson splits it into its irregular
 * and regular tables, and each row's play buttons speak the row's `reading`.
 */
export const DAYS_OF_MONTH: readonly DayOfMonth[] = [
    { number: 1, day: "1st", kanji: "一日", reading: "ついたち", romaji: "tsuitachi", irregular: true },
    { number: 2, day: "2nd", kanji: "二日", reading: "ふつか", romaji: "futsuka", irregular: true },
    { number: 3, day: "3rd", kanji: "三日", reading: "みっか", romaji: "mikka", irregular: true },
    { number: 4, day: "4th", kanji: "四日", reading: "よっか", romaji: "yokka", irregular: true },
    { number: 5, day: "5th", kanji: "五日", reading: "いつか", romaji: "itsuka", irregular: true },
    { number: 6, day: "6th", kanji: "六日", reading: "むいか", romaji: "muika", irregular: true },
    { number: 7, day: "7th", kanji: "七日", reading: "なのか", romaji: "nanoka", irregular: true },
    { number: 8, day: "8th", kanji: "八日", reading: "ようか", romaji: "yōka", irregular: true },
    { number: 9, day: "9th", kanji: "九日", reading: "ここのか", romaji: "kokonoka", irregular: true },
    { number: 10, day: "10th", kanji: "十日", reading: "とおか", romaji: "tōka", irregular: true },
    { number: 11, day: "11th", kanji: "十一日", reading: "じゅういちにち", romaji: "jūichinichi", irregular: false },
    { number: 12, day: "12th", kanji: "十二日", reading: "じゅうににち", romaji: "jūninichi", irregular: false },
    { number: 13, day: "13th", kanji: "十三日", reading: "じゅうさんにち", romaji: "jūsannichi", irregular: false },
    { number: 14, day: "14th", kanji: "十四日", reading: "じゅうよっか", romaji: "jūyokka", irregular: true },
    { number: 15, day: "15th", kanji: "十五日", reading: "じゅうごにち", romaji: "jūgonichi", irregular: false },
    { number: 16, day: "16th", kanji: "十六日", reading: "じゅうろくにち", romaji: "jūrokunichi", irregular: false },
    { number: 17, day: "17th", kanji: "十七日", reading: "じゅうしちにち", romaji: "jūshichinichi", irregular: false },
    { number: 18, day: "18th", kanji: "十八日", reading: "じゅうはちにち", romaji: "jūhachinichi", irregular: false },
    { number: 19, day: "19th", kanji: "十九日", reading: "じゅうくにち", romaji: "jūkunichi", irregular: false },
    { number: 20, day: "20th", kanji: "二十日", reading: "はつか", romaji: "hatsuka", irregular: true },
    { number: 21, day: "21st", kanji: "二十一日", reading: "にじゅういちにち", romaji: "nijūichinichi", irregular: false },
    { number: 22, day: "22nd", kanji: "二十二日", reading: "にじゅうににち", romaji: "nijūninichi", irregular: false },
    { number: 23, day: "23rd", kanji: "二十三日", reading: "にじゅうさんにち", romaji: "nijūsannichi", irregular: false },
    { number: 24, day: "24th", kanji: "二十四日", reading: "にじゅうよっか", romaji: "nijūyokka", irregular: true },
    { number: 25, day: "25th", kanji: "二十五日", reading: "にじゅうごにち", romaji: "nijūgonichi", irregular: false },
    { number: 26, day: "26th", kanji: "二十六日", reading: "にじゅうろくにち", romaji: "nijūrokunichi", irregular: false },
    { number: 27, day: "27th", kanji: "二十七日", reading: "にじゅうしちにち", romaji: "nijūshichinichi", irregular: false },
    { number: 28, day: "28th", kanji: "二十八日", reading: "にじゅうはちにち", romaji: "nijūhachinichi", irregular: false },
    { number: 29, day: "29th", kanji: "二十九日", reading: "にじゅうくにち", romaji: "nijūkunichi", irregular: false },
    { number: 30, day: "30th", kanji: "三十日", reading: "さんじゅうにち", romaji: "sanjūnichi", irregular: false },
    { number: 31, day: "31st", kanji: "三十一日", reading: "さんじゅういちにち", romaji: "sanjūichinichi", irregular: false },
];
